/**
 * Auto-fire for the touch controls (GDD §8, decision Q3; judgment call 29): the cannons fire while the first thing in
 * their line of fire, within tracer range, is an enemy or an intact bridge. A fuel barge in front blocks it, so
 * auto-fire never shoots a barge. Holding FIRE still shoots anything.
 */
import { config } from '../core/config.ts';
import { quatFromEulerYXZ, rotateByQuat, type Quat4, type Vec3 } from '../core/quat.ts';
import type { GameSim } from '../sim/context.ts';

/** Half the gap between the muzzles (src/rules/guns.ts) plus the extra width a tracer hits with. */
const LINE_HALF_WIDTH = 0.7 + 0.8;
/** The bridge's depth for tracer hits (src/rules/guns.ts). */
const BRIDGE_DEPTH = 6;

const q: Quat4 = [0, 0, 0, 1];
const fwd: Vec3 = [0, 0, 0];

/** True when auto-fire should shoot this step. */
export function autoFireTarget(sim: GameSim): boolean {
  const run = sim.run;
  const jet = sim.jet;
  if (!run.alive) return false;
  // Tracers fly 0.9 s at 300 m/s plus the jet's speed (GDD §4.2): about 310 m at cruise.
  const range = config.guns.tracerLife * (config.guns.tracerSpeedBonus + jet.speed);
  quatFromEulerYXZ(jet.pitch, jet.yaw, jet.roll, q);
  rotateByQuat(q, 0, 0, -1, fwd);
  if (fwd[2] >= -1e-6) return false;
  // Sideways drift of the line of fire per metre ahead (forward is −z).
  const slope = fwd[0] / -fwd[2];
  let nearest = Infinity;
  let target = false;
  const ents = sim.ents;
  for (let i = 0; i < ents.count; i++) {
    const e = ents.items[i];
    if (!e || e.dying > 0) continue;
    const ahead = jet.z - e.z;
    if (ahead + e.hl <= 0 || ahead - e.hl > range) continue;
    const x = jet.x + slope * ahead;
    if (Math.abs(x - e.x) >= e.hw + LINE_HALF_WIDTH) continue;
    const near = ahead - e.hl;
    if (near < nearest) {
      nearest = near;
      target = e.type !== 'fuel';
    }
  }
  const b = sim.bridge;
  if (b.intact) {
    const ahead = jet.z - b.z;
    const near = ahead - BRIDGE_DEPTH;
    if (ahead + BRIDGE_DEPTH > 0 && near < range && near < nearest) {
      const x = jet.x + slope * ahead;
      if (Math.abs(x - b.rootX) < b.span / 2) target = true;
    }
  }
  return target;
}
